/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * DataOps Widget Definitions
 *
 * Defines all widgets for the DataOps dashboard using the template system.
 * Widgets fetch data from the lens-dataops service via catalogAPI and runsAPI.
 */

import {
  createMetricWidget,
  createChartWidget,
  WIDGET_CATEGORIES,
  WIDGET_SIZES,
  WIDGET_ICONS,
  DATA_SOURCES,
} from '../widgetTemplates';
import { GREEN, RED, BLUE, CYAN, CYAN_DARK, SKY, SKY_DARK } from '../../../constants/chartColors';

const {
  FiDatabase,
  FiCheckCircle,
  FiXCircle,
  FiTrendingUp,
  FiRefreshCw,
  FiClock,
  FiBarChart2,
  FiUser,
  FiActivity,
  FiSettings,
  FiTarget,
  FiArrowUp,
  FiArrowDown,
  FiMaximize2,
  FiPlay,
  FiTag,
  FiFileText,
  FiUsers,
  FiLock,
  FiAlertTriangle,
} = WIDGET_ICONS;

/**
 * Total Datasets Widget
 * Shows all datasets tracked
 * Uses data from lens-dataops service
 */
export const totalDatasetsWidget = createMetricWidget({
  id: 'total-datasets',
  name: 'Total Datasets',
  description: 'All datasets tracked',
  icon: FiDatabase,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 300000,

  getData: (sourceData) => ({
    value: sourceData?.totalDatasets || 0,
    label: 'Total Datasets',
    format: 'number',
    color: 'aqua',
    link: '/dataops/catalog',
  }),
});

/**
 * Data Quality Score Widget
 * Shows overall data quality rating
 * Uses data from lens-dataops service
 */
export const dataQualityScoreWidget = createMetricWidget({
  id: 'data-quality-score',
  name: 'Data Quality Score',
  description: 'Overall data quality rating',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORIES.DATAOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 300000,

  getData: (sourceData) => ({
    value: sourceData?.dataQualityScore || 0,
    label: 'Data Quality Score',
    format: 'percentage',
    color: 'aqua',
    link: '/dataops/governance',
  }),
});

/**
 * Successful Runs Widget
 * Shows completed data job runs
 * Uses data from lens-dataops service
 */
export const successfulRunsWidget = createMetricWidget({
  id: 'successful-runs',
  name: 'Successful Runs',
  description: 'Completed data job runs',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => ({
    value: sourceData?.successfulRuns || 0,
    label: 'Successful Runs',
    format: 'number',
    color: 'aqua',
    link: '/dataops',
  }),
});

/**
 * Failed Runs Widget
 * Shows failed job executions
 * Uses data from lens-dataops service
 */
export const failedRunsWidget = createMetricWidget({
  id: 'failed-runs',
  name: 'Failed Runs',
  description: 'Failed job executions',
  icon: FiXCircle,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => ({
    value: sourceData?.failedRuns || 0,
    label: 'Failed Runs',
    format: 'number',
    color: 'darkcyan',
    link: '/dataops',
  }),
});

/**
 * Pipeline Runs Chart Widget
 * Shows run status distribution over time
 */
export const pipelineRunsChartWidget = createChartWidget({
  id: 'pipeline-runs-chart',
  name: 'Pipeline Runs',
  description: 'Run status distribution over time',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.DATAOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: true,
  refreshRate: 300000,

  getData: (sourceData) => {
    const pipelineRuns = sourceData?.pipelineRuns || [];

    // Use the config from fetcher or default to stacked bar chart
    const config = sourceData?.pipelineChartConfig || {
      chartType: 'bar',
      xKey: 'date',
      yKeys: ['successful', 'failed'],
      colors: [GREEN, RED],
      stacked: true,
    };

    return {
      chartType: config.chartType,
      data: pipelineRuns,
      xKey: config.xKey,
      yKeys: config.yKeys,
      colors: config.colors,
      stacked: config.stacked,
      link: '/dataops/catalog',
    };
  },
});

// ============================================
// Additional DataOps Widgets (Phase 10)
// ============================================

/**
 * Schema Changes Widget
 * Shows recent schema modifications
 */
export const schemaChangesWidget = createMetricWidget({
  id: 'schema-changes',
  name: 'Schema Changes',
  description: 'Recent schema modifications',
  icon: FiRefreshCw,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.schemaChanges || sourceData?.schema_changes_count || 0,
    label: 'Schema Changes',
    format: 'number',
    color: 'aqua',
    link: '/dataops',
  }),
});

/**
 * Average Run Duration Widget
 * Shows average pipeline runtime
 */
export const avgRunDurationDataopsWidget = createMetricWidget({
  id: 'avg-run-duration',
  name: 'Avg Run Duration',
  description: 'Average pipeline runtime',
  icon: FiClock,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const runs = sourceData?.runs || sourceData?.pipeline_runs || [];
    const avgDuration = runs.length > 0
      ? runs.reduce((sum: number, r: Record<string, unknown>) => sum + (Number(r.duration_ms) || Number(r.duration) || 0), 0) / runs.length
      : sourceData?.avgRunDuration || 0;
    return {
      value: Math.round(avgDuration),
      label: 'Avg Duration',
      format: 'duration',
      color: 'cyan',
      link: '/dataops',
    };
  },
});

/**
 * Data Quality Trend Widget
 * Shows quality score over time
 */
export const dataQualityTrendWidget = createChartWidget({
  id: 'data-quality-trend',
  name: 'Quality Trend',
  description: 'Quality score over time',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.DATAOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const trend = sourceData?.qualityTrend || sourceData?.quality_trend || [];
    const data = trend.length > 0 ? trend : [
      { date: 'Mon', score: 92 },
      { date: 'Tue', score: 94 },
      { date: 'Wed', score: 91 },
      { date: 'Thu', score: 95 },
      { date: 'Fri', score: 96 },
    ];
    return {
      chartType: 'line',
      data,
      xKey: 'date',
      yKey: 'score',
      color: CYAN,
      link: '/dataops/governance',
    };
  },
});

/**
 * Freshness Alerts Widget
 * Shows stale data warnings
 */
export const freshnessAlertsWidget = createMetricWidget({
  id: 'freshness-alerts',
  name: 'Freshness Alerts',
  description: 'Stale data warnings',
  icon: FiClock,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.freshnessAlerts || sourceData?.stale_datasets || 0,
    label: 'Freshness Alerts',
    format: 'number',
    color: 'amber',
    link: '/dataops/governance',
  }),
});

/**
 * Runs by Status Widget
 * Shows Success/Failed/Running breakdown
 */
export const runsByStatusWidget = createChartWidget({
  id: 'runs-by-status',
  name: 'Runs by Status',
  description: 'Success/Failed/Running breakdown',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.DATAOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    // Use pre-computed runsByStatus from fetcher (uses 'name' key for ChartWidget)
    const data = sourceData?.runsByStatus || [
      { name: 'Success', count: sourceData?.successfulRuns || 0 },
      { name: 'Failed', count: sourceData?.failedRuns || 0 },
      { name: 'Running', count: sourceData?.runningRuns || 0 },
    ];
    return {
      chartType: 'donut',
      data,
      dataKey: 'count',
      nameKey: 'name',
      colors: [GREEN, RED, BLUE],
      link: '/dataops/catalog',
    };
  },
});

/**
 * Pipeline Success Rate Widget
 * Shows successful run percentage
 */
export const pipelineSuccessRateWidget = createMetricWidget({
  id: 'pipeline-success-rate',
  name: 'Pipeline Success',
  description: 'Successful run percentage',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const success = sourceData?.successfulRuns || 0;
    const failed = sourceData?.failedRuns || 0;
    const total = success + failed;
    const rate = total > 0 ? (success / total) * 100 : 0;
    return {
      value: Math.round(rate * 10) / 10,
      label: 'Success Rate',
      format: 'percentage',
      color: 'green',
      link: '/dataops',
    };
  },
});

/**
 * Data Volume Widget
 * Shows total data processed (TB)
 */
export const dataVolumeWidget = createMetricWidget({
  id: 'data-volume',
  name: 'Data Volume',
  description: 'Total data processed (TB)',
  icon: FiDatabase,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.dataVolume || sourceData?.total_volume_tb || 0,
    label: 'Data Volume',
    format: 'number',
    color: 'blue',
    displayValue: `${sourceData?.dataVolume || sourceData?.total_volume_tb || 0} TB`,
    link: '/dataops/catalog',
  }),
});

/**
 * Dataset Ownership Widget
 * Shows datasets by owner
 */
export const datasetOwnershipWidget = createChartWidget({
  id: 'dataset-ownership',
  name: 'Dataset Ownership',
  description: 'Datasets by owner',
  icon: FiUser,
  category: WIDGET_CATEGORIES.DATAOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => {
    const ownership = sourceData?.datasetOwnership || sourceData?.ownership_breakdown || [];
    const data = ownership.length > 0 ? ownership : [
      { owner: 'Data Engineering', count: 45 },
      { owner: 'Analytics', count: 32 },
      { owner: 'ML Team', count: 18 },
      { owner: 'Unassigned', count: 8 },
    ];
    return {
      chartType: 'horizontalBar',
      data,
      xKey: 'owner',
      yKey: 'count',
      color: CYAN,
      link: '/dataops/governance',
    };
  },
});

/**
 * Column Level Quality Widget
 * Shows column-level DQ score
 */
export const columnLevelQualityWidget = createMetricWidget({
  id: 'column-level-quality',
  name: 'Column Quality',
  description: 'Column-level DQ score',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.columnQuality || sourceData?.column_quality_score || 0,
    label: 'Column Quality',
    format: 'percentage',
    color: 'aqua',
    link: '/dataops/governance',
  }),
});

/**
 * Events Today Widget
 * Shows pipeline events count
 */
export const eventsTodayWidget = createMetricWidget({
  id: 'events-today',
  name: 'Events Today',
  description: 'Pipeline events count',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.eventsToday || sourceData?.events_count || 0,
    label: 'Events Today',
    format: 'number',
    color: 'blue',
    link: '/dataops',
  }),
});

/**
 * Total Jobs Widget
 * Shows all data jobs monitored
 */
export const totalJobsWidget = createMetricWidget({
  id: 'total-jobs',
  name: 'Total Jobs',
  description: 'All data jobs monitored',
  icon: FiSettings,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.totalJobs || sourceData?.jobs_count || 0,
    label: 'Total Jobs',
    format: 'number',
    color: 'aqua',
    link: '/dataops',
  }),
});

/**
 * Active Pipelines Widget
 * Shows currently running pipelines
 */
export const activePipelinesWidget = createMetricWidget({
  id: 'active-pipelines',
  name: 'Active Pipelines',
  description: 'Currently running',
  icon: FiRefreshCw,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.activePipelines || sourceData?.running_pipelines || 0,
    label: 'Active Pipelines',
    format: 'number',
    color: 'cyan',
    link: '/dataops',
  }),
});

/**
 * Degraded Datasets Widget
 * Shows count of datasets with quality or freshness issues
 */
export const degradedDatasetsWidget = createMetricWidget({
  id: 'degraded-datasets',
  name: 'Degraded Datasets',
  description: 'Quality or freshness issues',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.degradedDatasets || 0,
    label: 'Degraded Datasets',
    format: 'number',
    color: 'amber',
    link: '/dataops/catalog',
  }),
});

/**
 * Datasets by Type Widget
 * Shows dataset type distribution
 */
export const datasetsByTypeWidget = createChartWidget({
  id: 'datasets-by-type',
  name: 'Datasets by Type',
  description: 'Dataset type distribution',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.DATAOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => {
    const typeBreakdown = sourceData?.datasetsByType || sourceData?.type_distribution || [];
    const data = typeBreakdown.length > 0 ? typeBreakdown : [
      { type: 'Tables', count: 65 },
      { type: 'Views', count: 28 },
      { type: 'Streams', count: 12 },
      { type: 'Files', count: 8 },
    ];
    return {
      chartType: 'pie',
      data,
      dataKey: 'count',
      nameKey: 'type',
      colors: [CYAN, SKY, CYAN_DARK, SKY_DARK],
      link: '/dataops/catalog',
    };
  },
});

/**
 * Data Completeness Widget
 * Shows data completeness metric
 */
export const completenessPercentageWidget = createMetricWidget({
  id: 'completeness-percentage',
  name: 'Data Completeness',
  description: 'Data completeness metric',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.completeness || sourceData?.completeness_score || 0,
    label: 'Completeness',
    format: 'percentage',
    color: 'green',
    link: '/dataops/governance',
  }),
});

/**
 * Data Accuracy Widget
 * Shows data accuracy score
 */
export const accuracyPercentageWidget = createMetricWidget({
  id: 'accuracy-percentage',
  name: 'Data Accuracy',
  description: 'Data accuracy score',
  icon: FiTarget,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.accuracy || sourceData?.accuracy_score || 0,
    label: 'Accuracy',
    format: 'percentage',
    color: 'blue',
    link: '/dataops/governance',
  }),
});

/**
 * Data Timeliness Widget
 * Shows data freshness metric
 */
export const timelinessPercentageWidget = createMetricWidget({
  id: 'timeliness-percentage',
  name: 'Data Timeliness',
  description: 'Data freshness metric',
  icon: FiClock,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.timeliness || sourceData?.timeliness_score || 0,
    label: 'Timeliness',
    format: 'percentage',
    color: 'cyan',
    link: '/dataops/governance',
  }),
});

/**
 * Data Consistency Widget
 * Shows consistency score
 */
export const consistencyPercentageWidget = createMetricWidget({
  id: 'consistency-percentage',
  name: 'Data Consistency',
  description: 'Consistency score',
  icon: FiRefreshCw,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.consistency || sourceData?.consistency_score || 0,
    label: 'Consistency',
    format: 'percentage',
    color: 'aqua',
    link: '/dataops/governance',
  }),
});

/**
 * Failed Quality Checks Widget
 * Shows recent quality failures
 */
export const failedQualityChecksWidget = createMetricWidget({
  id: 'failed-quality-checks',
  name: 'Failed Quality Checks',
  description: 'Recent quality failures',
  icon: FiXCircle,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.failedQualityChecks || sourceData?.failed_checks || 0,
    label: 'Failed Checks',
    format: 'number',
    color: 'red',
    link: '/dataops/governance',
  }),
});

/**
 * Upstream Dependencies Widget
 * Shows number of data sources
 */
export const upstreamDependenciesWidget = createMetricWidget({
  id: 'upstream-dependencies',
  name: 'Upstream Dependencies',
  description: 'Number of data sources',
  icon: FiArrowUp,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.upstreamDependencies || sourceData?.upstream_count || 0,
    label: 'Upstream',
    format: 'number',
    color: 'blue',
    link: '/dataops/lineage',
  }),
});

/**
 * Downstream Dependencies Widget
 * Shows number of consumers
 */
export const downstreamDependenciesWidget = createMetricWidget({
  id: 'downstream-dependencies',
  name: 'Downstream Dependencies',
  description: 'Number of consumers',
  icon: FiArrowDown,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.downstreamDependencies || sourceData?.downstream_count || 0,
    label: 'Downstream',
    format: 'number',
    color: 'cyan',
    link: '/dataops/lineage',
  }),
});

/**
 * Lineage Depth Widget
 * Shows transformation levels
 */
export const lineageDepthWidget = createMetricWidget({
  id: 'lineage-depth',
  name: 'Lineage Depth',
  description: 'Transformation levels',
  icon: FiMaximize2,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.lineageDepth || sourceData?.max_depth || 0,
    label: 'Max Depth',
    format: 'number',
    color: 'aqua',
    link: '/dataops/lineage',
  }),
});

/**
 * Running Jobs Widget
 * Shows currently executing jobs
 */
export const runningJobsWidget = createMetricWidget({
  id: 'running-jobs',
  name: 'Running Jobs',
  description: 'Currently executing',
  icon: FiPlay,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.runningJobs || sourceData?.active_jobs || 0,
    label: 'Running Jobs',
    format: 'number',
    color: 'green',
    link: '/dataops',
  }),
});

/**
 * Datasets with Owners Widget
 * Shows assigned ownership count
 */
export const datasetsWithOwnersWidget = createMetricWidget({
  id: 'datasets-with-owners',
  name: 'Datasets with Owners',
  description: 'Assigned ownership',
  icon: FiUser,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.datasetsWithOwners || sourceData?.owned_datasets || 0,
    label: 'With Owners',
    format: 'number',
    color: 'aqua',
    link: '/dataops/catalog',
  }),
});

/**
 * Active Tags Widget
 * Shows classification tags count
 */
export const activeTagsWidget = createMetricWidget({
  id: 'active-tags',
  name: 'Active Tags',
  description: 'Classification tags',
  icon: FiTag,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.activeTags || sourceData?.tags_count || 0,
    label: 'Active Tags',
    format: 'number',
    color: 'blue',
    link: '/dataops/catalog',
  }),
});

/**
 * Governance Rules Widget
 * Shows active policies count
 */
export const governanceRulesWidget = createMetricWidget({
  id: 'governance-rules',
  name: 'Governance Rules',
  description: 'Active policies',
  icon: FiFileText,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.governanceRules || sourceData?.policies_count || 0,
    label: 'Rules',
    format: 'number',
    color: 'aqua',
    link: '/dataops/governance',
  }),
});

/**
 * Data Stewards Widget
 * Shows steward count
 */
export const dataStewardsWidget = createMetricWidget({
  id: 'data-stewards',
  name: 'Data Stewards',
  description: 'Steward count',
  icon: FiUsers,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.dataStewards || sourceData?.stewards_count || 0,
    label: 'Stewards',
    format: 'number',
    color: 'cyan',
    link: '/dataops/governance',
  }),
});

/**
 * PII Datasets Widget
 * Shows sensitive data tracking
 */
export const piiDatasetsWidget = createMetricWidget({
  id: 'pii-datasets',
  name: 'PII Datasets',
  description: 'Sensitive data tracking',
  icon: FiLock,
  category: WIDGET_CATEGORIES.DATAOPS,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.piiDatasets || sourceData?.sensitive_datasets || 0,
    label: 'PII Datasets',
    format: 'number',
    color: 'amber',
    link: '/dataops/governance',
  }),
});

/**
 * Events by Type Widget
 * Shows event type distribution
 */
export const eventsByTypeWidget = createChartWidget({
  id: 'events-by-type',
  name: 'Events by Type',
  description: 'Event type distribution',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.DATAOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.DATAOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const eventTypes = sourceData?.eventsByType || sourceData?.event_distribution || [];
    const data = eventTypes.length > 0 ? eventTypes : [
      { type: 'Schema Change', count: 15 },
      { type: 'Quality Alert', count: 8 },
      { type: 'Run Complete', count: 120 },
      { type: 'Run Failed', count: 12 },
    ];
    return {
      chartType: 'bar',
      data,
      xKey: 'type',
      yKey: 'count',
      color: CYAN,
      link: '/dataops/catalog',
    };
  },
});

/**
 * Export all DataOps widgets as an array
 */
export const DATAOPS_WIDGETS = [
  // Original widgets
  totalDatasetsWidget,
  dataQualityScoreWidget,
  successfulRunsWidget,
  failedRunsWidget,
  pipelineRunsChartWidget,
  // Additional widgets (Phase 10)
  schemaChangesWidget,
  avgRunDurationDataopsWidget,
  dataQualityTrendWidget,
  freshnessAlertsWidget,
  runsByStatusWidget,
  pipelineSuccessRateWidget,
  dataVolumeWidget,
  datasetOwnershipWidget,
  columnLevelQualityWidget,
  eventsTodayWidget,
  totalJobsWidget,
  activePipelinesWidget,
  degradedDatasetsWidget,
  datasetsByTypeWidget,
  completenessPercentageWidget,
  accuracyPercentageWidget,
  timelinessPercentageWidget,
  consistencyPercentageWidget,
  failedQualityChecksWidget,
  upstreamDependenciesWidget,
  downstreamDependenciesWidget,
  lineageDepthWidget,
  runningJobsWidget,
  datasetsWithOwnersWidget,
  activeTagsWidget,
  governanceRulesWidget,
  dataStewardsWidget,
  piiDatasetsWidget,
  eventsByTypeWidget,
];

/**
 * Export widgets as a map by ID for quick lookup
 */
export const DATAOPS_WIDGETS_MAP = DATAOPS_WIDGETS.reduce((acc: Record<string, any>, widget) => {
  acc[widget.id] = widget;
  return acc;
}, {} as Record<string, any>);

export default DATAOPS_WIDGETS;
